// Type-only, deliberately: `plate.component` imports `findPlateCountry` from this file, so a
// value import here would close a runtime cycle. `import type` is erased at compile time.
import type { RegionSeparator } from './plate.component';
import { PlateRegionSet } from './plate-emblems';
import { AT_REGIONS } from './AT/at-districts';

export interface PlateCountry {
  /** ISO-ish country code; also the `JKU.COUNTRIES.*` i18n key suffix. */
  code: string;
  /** Letter(s) shown on the EU blue band. */
  euBand: string;
  /**
   * Region codes this country issues and the emblem each carries. Optional: a country without it
   * still renders correctly, it just draws a neutral shield instead of real arms and cannot
   * validate the region code.
   *
   * To add a country: write `xx-districts.ts` exporting a {@link PlateRegionSet}, then set it
   * here. Nothing in `PlateComponent` needs to change — see `plate-emblems.ts`.
   */
  regions?: PlateRegionSet;
  /**
   * Longest district/region prefix this country uses, in letters. 0 = no such prefix, so the
   * number is never split.
   *
   * This genuinely differs per country and cannot be shared: Germany allows three letters
   * ("GAP", "BOR"), so applying Austria's two-letter cap there would split "GAP" into "GA".
   */
  regionLength: number;
  /** How the region is set off from the rest of the number when displayed. */
  regionSeparator: RegionSeparator;
}

/**
 * Per-country plate rules, shared by the JKU webshop and "My License Plates" so a plate renders
 * identically wherever it appears.
 *
 * `regionLength: 0` marks the countries whose plates carry no district prefix at all — Czech
 * plates are digit-led ("1AB 2345"), Hungarian ones use a series code rather than a district, and
 * Italy dropped province codes in 1994. Splitting those would invent a region that does not exist.
 */
export const PLATE_COUNTRIES: PlateCountry[] = [
  { code: 'AT', euBand: 'A', regionLength: 2, regionSeparator: 'emblem', regions: AT_REGIONS },
  // DE/CH/SI have region codes and arms too, but no region set yet: they render a neutral shield
  // until someone adds `de-districts.ts` / `ch-cantons.ts` / `si-districts.ts`.
  { code: 'DE', euBand: 'D', regionLength: 3, regionSeparator: 'emblem' },
  { code: 'CZ', euBand: 'CZ', regionLength: 0, regionSeparator: 'none' },
  { code: 'SK', euBand: 'SK', regionLength: 2, regionSeparator: 'dash' },
  { code: 'HU', euBand: 'H', regionLength: 0, regionSeparator: 'none' },
  { code: 'SI', euBand: 'SLO', regionLength: 2, regionSeparator: 'emblem' },
  { code: 'CH', euBand: 'CH', regionLength: 2, regionSeparator: 'emblem' },
  { code: 'IT', euBand: 'I', regionLength: 0, regionSeparator: 'none' }
];

/**
 * Rule for a stored country code. Unknown codes (a plate registered before the dropdown covered
 * that country, say) fall back to "no region": rendering the number undivided is always safe,
 * inventing a split is not.
 */
export function findPlateCountry(countryCode: string | null | undefined): PlateCountry | undefined {
  const code = countryCode?.trim().toUpperCase();
  return code ? PLATE_COUNTRIES.find(c => c.code === code) : undefined;
}

/**
 * Canonical plate form used everywhere the plate is shown, compared or stored:
 * uppercased with all whitespace and hyphens removed. This keeps the live preview, the "repeat"
 * match check and the submitted value in one compact form that lines up with the separator-free
 * reading the gate cameras (ANPR) produce.
 */
export function normalizePlate(value: string | null | undefined): string {
  return (value ?? '').toUpperCase().replace(PLATE_SEPARATORS, '');
}

/**
 * The separators stripped to reach the canonical form. The input filter already drops them, but
 * values from other sources (stored rows, imports, pasted programmatic values) may still carry them.
 *
 * Kept byte-for-byte in step with the backend's `LicensePlatePolicy.Separators`: the two sides must
 * agree on what "the same plate" means, or a value normalized here fails to match the row stored
 * there. Dot and slash are included for that reason — dropping them from only one side is what makes
 * a plate silently unmatchable.
 */
const PLATE_SEPARATORS = /[\s\-./]/g;

/**
 * Characters a plate field accepts while typing: A–Z, the German umlauts Ä/Ö/Ü, and digits — nothing
 * else. Separators (space, hyphen, dot, slash) are dropped at the keystroke too, so what the parker
 * sees is already the separator-free form the gate cameras read. ß is excluded on purpose: it has no
 * single-letter upper case, so upper-casing it would change the field's length under the caret.
 *
 * Stricter than the backend `LicensePlatePolicy` pattern (which still accepts separators and any
 * script's letters for import adapters), never looser — the field cannot accept anything the API
 * would reject.
 */
const REJECTED_PLATE_CHARS = /[^A-Za-z0-9ÄÖÜäöü]/g;

/** Longest plate the backend accepts. Mirrors `LicensePlatePolicy`. */
export const MAX_PLATE_LENGTH = 32;

/**
 * Shortest plate the backend accepts. Mirrors `LicensePlatePolicy.FullNumberMinLength`: the gate
 * treats anything shorter as an unlicensed car, so it could never be recognised.
 */
export const MIN_PLATE_LENGTH = 3;

/**
 * Whether a plate looks like a real plate: {@link MIN_PLATE_LENGTH}–{@link MAX_PLATE_LENGTH}
 * characters once normalized, with at least one letter and one digit. Mirrors
 * `LicensePlatePolicy.IsValidFormat`, which rejects a district code typed as its own plate ("AM")
 * or a bare number ("985").
 */
export function isValidPlateFormat(value: string | null | undefined): boolean {
  const plate = normalizePlate(value);
  return plate.length >= MIN_PLATE_LENGTH
    && plate.length <= MAX_PLATE_LENGTH
    && /\p{L}/u.test(plate)
    && /\d/.test(plate);
}

/**
 * Input filter for plate fields: strips everything {@link REJECTED_PLATE_CHARS} matches, upper-cases,
 * and clamps the length. Upper-casing never changes the length for the
 * allowed set, so callers can restore the caret by counting rejected characters.
 */
export function sanitizePlateInput(value: string | null | undefined): string {
  return (value ?? '').replace(REJECTED_PLATE_CHARS, '').toUpperCase().slice(0, MAX_PLATE_LENGTH);
}

/**
 * Extracts the district/region code from what the user typed, so it can be stored separately
 * (`LicensePlateDto.state`) and used to render `L ⬥ 1234PP` while the stored number stays the
 * separator-free `L1234PP` the gate cameras read.
 *
 * A separator the user typed themselves is authoritative — "L-1234PP" and "M GAP 123" state
 * plainly where the district code ends — but it is still held to the country's maximum, so a
 * stray hyphen cannot invent a five-letter district. Without a separator we fall back to the
 * leading letters, capped at `maxLength`.
 *
 * Returns '' when the country has no district prefix, or when the split is not credible (no
 * leading letters, or nothing left after them). The caller then stores no region and the plate
 * renders undivided.
 */
export function extractRegion(
  rawInput: string,
  maxLength: number,
  regions?: PlateRegionSet
): string {
  if (maxLength <= 0) {
    return '';
  }

  const separated = new RegExp(`^\\s*([\\p{L}]{1,${maxLength}})\\s*[-\\s/.]\\s*(.+)$`, 'u').exec(rawInput);
  if (separated) {
    return normalizePlate(separated[1]);
  }

  const compact = normalizePlate(rawInput);

  // Without a separator the split is a guess, and taking the letters greedily gets it wrong for
  // any short district followed by more letters: "WABC12" is Vienna's "W" + "ABC12", but a greedy
  // two-letter read makes it "WA", which is not a district at all. So when we know the country's
  // districts, prefer the *longest prefix that is actually one* — longest first, so "LL" still
  // beats "L" for "LL123AB". Only if none matches do we fall back to the greedy read, which keeps
  // special and foreign series rendering with a plausible split rather than none.
  if (regions) {
    for (let length = Math.min(maxLength, compact.length - 1); length >= 1; length--) {
      const candidate = compact.slice(0, length);
      if (/^\p{L}+$/u.test(candidate) && regions.districts[candidate]) {
        return candidate;
      }
    }
  }

  const leadingLetters = new RegExp(`^([\\p{L}]{1,${maxLength}})(.+)$`, 'u').exec(compact);
  return leadingLetters ? leadingLetters[1] : '';
}
